import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/rootReducer';
import { AccessManifest, AuthState } from '@/services/api.types';
import Token from '@/utils/token';
import { PERMISSIONS } from '@/constants/permissions';

const initialState: AuthState = {
  token: {
    access: '',
    refresh: '',
  },
};

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setTokens: (state, { payload }: PayloadAction<AuthState['token']>) => {
      state.token.access = payload.access;
      state.token.refresh = payload.refresh;
      if (payload.access) {
        const jwt = Token.parseJwt(payload.access);
        state.jwtPayload = jwt.payload;
      } else {
        // token cleared (logout / 401) — drop the stale capability manifest so a
        // subsequent login never briefly gates on the previous user's accesses.
        state.accessManifest = undefined;
      }
      Token.persistToken(payload);
    },
    // capability manifest from GET /v1/access (batch 001). Fetched per session by
    // the auth guard and stored here — permissions/roles no longer ride in the JWT.
    setAccess: (
      state,
      { payload }: PayloadAction<AccessManifest | undefined>
    ) => {
      state.accessManifest = payload;
    },
  },
  // No extraReducers. All eight matchers here listened on the legacy oauth/user
  // endpoints that ADR-7 deleted (exchangeMfaToken / exchangeAuthToken /
  // confirmPolicy / activateAuthToken / initializePassword / resetNewPassword /
  // resetV2NewPassword / initializeV2Password). Every one of them wrote
  // token.initialAccess or token.temporary, which nothing in the repo ever READ —
  // so removing them changes no behaviour, and leaving them would have been worse
  // than dead code: `authApi.endpoints.<deleted>.matchFulfilled` is a TypeError at
  // slice-construction time, i.e. the whole app fails to boot.
  //
  // Token landing now has exactly one path for every flow: setTokens, called from
  // advanceLoginStep (login-step.ts) and from the renewal in authed.query.ts.
});

export const { setTokens, setAccess } = slice.actions;

export const selectAuthToken = (state: RootState): AuthState['token'] =>
  state.auth.token;
export const selectJwtPayload = (state: RootState) => state.auth.jwtPayload;
export const selectAccessManifest = (state: RootState) =>
  state.auth.accessManifest;
// Source of truth for UI gating: business-capability slugs from GET /v1/access
// (batch 001). `undefined` = manifest not yet loaded this session; `[]` = loaded,
// caller holds none. Guards must treat those two distinctly (see authed-container).
export const selectUserPermissions = (
  state: RootState
): PERMISSIONS[] | undefined => state.auth.accessManifest?.accesses;
export const selectUserRoles = (state: RootState) =>
  state.auth.accessManifest?.roles ?? [];

export default slice.reducer;
